import { type KeyboardEvent, type PointerEvent, useState } from 'react';

import { CurrencyAmountInput } from '../../../shared/ui';

import {
  AcceptIcon,
  ActionButton,
  ActionRow,
  CancelIcon,
  EditButton,
  EditButtonSlot,
  EditIcon,
  EditModeRoot,
  Field,
  FieldInput,
  FieldLabel,
  FieldLabelRow,
  FieldSurface,
  FieldValue,
} from './my-page-profile-edit-field.styles';
import { MyPageProfileGoalTipToggleButton } from './my-page-profile-goal-tip-toggle-button';

type MyPageProfileEditFieldTypographyVariant = 'compact' | 'default';

type MyPageProfileEditFieldProps = {
  currencyPrefix?: string | null;
  infoAriaLabel?: string;
  label: string;
  maxLength?: number;
  onInfoPress?: () => void;
  onValueSave?: (nextValue: string) => void;
  thousandsSeparator?: string | null;
  typographyVariant?: MyPageProfileEditFieldTypographyVariant;
  value: string;
};

export function MyPageProfileEditField({
  currencyPrefix,
  infoAriaLabel,
  label,
  maxLength,
  onInfoPress,
  onValueSave,
  thousandsSeparator,
  typographyVariant = 'default',
  value,
}: MyPageProfileEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState(value);

  function startEditing() {
    setEditingValue(value);
    setIsEditing(true);
  }

  function cancelEditing() {
    setEditingValue(value);
    setIsEditing(false);
  }

  function saveEditing() {
    onValueSave?.(editingValue);
    setIsEditing(false);
  }

  function preventButtonPointerDownDefault(
    event: PointerEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();
  }

  function onCurrencyInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEditing();

      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
    }
  }

  function onGoalInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEditing();

      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
    }
  }
  return (
    <Field>
      <FieldLabelRow>
        <FieldLabel>{label}</FieldLabel>
        {infoAriaLabel && onInfoPress && (
          <MyPageProfileGoalTipToggleButton
            ariaLabel={infoAriaLabel}
            onPress={onInfoPress}
          />
        )}
      </FieldLabelRow>
      <FieldSurface $isEditing={isEditing}>
        {isEditing ? (
          <EditModeRoot>
            {currencyPrefix ? (
              <CurrencyAmountInput
                dataUi="my-page-profile-edit-field-currency-input"
                currencyPrefix={currencyPrefix}
                value={editingValue}
                onValueChange={setEditingValue}
                onKeyDown={onCurrencyInputKeyDown}
                thousandsSeparator={thousandsSeparator ?? ''}
                variant={typographyVariant}
              />
            ) : (
              <FieldInput
                $typographyVariant={typographyVariant}
                maxLength={maxLength}
                value={editingValue}
                onChange={event => setEditingValue(event.target.value)}
                onKeyDown={onGoalInputKeyDown}
              />
            )}
            <ActionRow>
              <ActionButton
                type="button"
                $tone="accept"
                aria-label={`${label}: save`}
                onPointerDown={preventButtonPointerDownDefault}
                onClick={saveEditing}
              >
                <AcceptIcon />
              </ActionButton>
              <ActionButton
                type="button"
                $tone="cancel"
                aria-label={`${label}: cancel`}
                onPointerDown={preventButtonPointerDownDefault}
                onClick={cancelEditing}
              >
                <CancelIcon />
              </ActionButton>
            </ActionRow>
          </EditModeRoot>
        ) : (
          <>
            <FieldValue $typographyVariant={typographyVariant}>
              {value}
            </FieldValue>
            <EditButtonSlot>
              <EditButton
                type="button"
                aria-label={`${label}: edit`}
                onClick={startEditing}
              >
                <EditIcon />
              </EditButton>
            </EditButtonSlot>
          </>
        )}
      </FieldSurface>
    </Field>
  );
}
